import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

const REASONS = [
  { key: "too_expensive", labelKey: "profile.cancelReasonExpensive" },
  { key: "missing_features", labelKey: "profile.cancelReasonMissing" },
  { key: "switched_service", labelKey: "profile.cancelReasonSwitched" },
  { key: "unused", labelKey: "profile.cancelReasonUnused" },
  { key: "too_complex", labelKey: "profile.cancelReasonComplex" },
  { key: "low_quality", labelKey: "profile.cancelReasonQuality" },
  { key: "customer_service", labelKey: "profile.cancelReasonService" },
  { key: "other", labelKey: "profile.cancelReasonOther" },
];

const formatDate = (iso, locale) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(locale || undefined, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function CancelSubscriptionModal({
  open,
  onOpenChange,
  subscription,
  onCancelled,
}) {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [endDateIso, setEndDateIso] = useState(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setFeedback("");
      setSubmitting(false);
      setDone(false);
      setEndDateIso(subscription?.current_period_end_iso || null);
    }
  }, [open, subscription]);

  const previewEnd = formatDate(
    subscription?.current_period_end_iso,
    i18n.language
  );

  const handleConfirm = async () => {
    if (!reason) {
      toast.error(t("profile.cancelReasonRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/cancel-subscription", {
        reason,
        feedback: feedback.trim(),
      });
      setEndDateIso(data.current_period_end_iso);
      setDone(true);
      toast.success(t("profile.cancelDone"));
      onCancelled?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (submitting ? null : onOpenChange(v))}>
      <DialogContent
        data-testid="cancel-sub-modal"
        className="max-w-lg rounded-sm"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter">
            {done ? t("profile.cancelDone") : t("profile.cancelTitle")}
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 border border-emerald-200 bg-emerald-50 rounded-sm p-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-900">
                {t("profile.cancelScheduledDesc", {
                  date: formatDate(endDateIso, i18n.language),
                })}
              </div>
            </div>
            <Button
              data-testid="cancel-sub-close-btn"
              onClick={() => onOpenChange(false)}
              className="w-full rounded-sm bg-neutral-900 hover:bg-neutral-800 text-white h-11"
            >
              {t("common.close") || "Fermer"}
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <p className="text-sm text-neutral-600">
              {t("profile.cancelSubtitle")}
            </p>

            {/* End-date preview */}
            <div className="flex items-start gap-3 border border-stone-200 bg-stone-50 rounded-sm p-4">
              <CalendarClock className="w-5 h-5 text-[#E05236] shrink-0 mt-0.5" />
              <div className="space-y-1 text-sm">
                <div className="font-semibold text-neutral-900">
                  {t("profile.cancelInfoTitle")}
                </div>
                <ul className="list-disc list-inside text-neutral-700 space-y-0.5">
                  {subscription?.current_period_end_iso && (
                    <li>
                      {t("profile.cancelInfoBullet1", { date: previewEnd })}
                    </li>
                  )}
                  <li>{t("profile.cancelInfoBullet2")}</li>
                  <li>{t("profile.cancelInfoBullet3")}</li>
                </ul>
              </div>
            </div>

            {/* Reason radio */}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] mb-2 block">
                {t("profile.cancelReasonLabel")}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {REASONS.map((r) => {
                  const active = reason === r.key;
                  return (
                    <label
                      key={r.key}
                      data-testid={`cancel-reason-${r.key}`}
                      className={`flex items-center gap-2 border rounded-sm px-3 py-2 cursor-pointer transition-colors ${
                        active
                          ? "border-[#E05236] bg-orange-50"
                          : "border-stone-200 hover:border-neutral-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name="cancel-reason"
                        value={r.key}
                        checked={active}
                        onChange={() => setReason(r.key)}
                        className="accent-[#E05236]"
                      />
                      <span className="text-sm">{t(r.labelKey)}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Optional feedback */}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] mb-2 block">
                {t("profile.cancelFeedbackLabel")}
              </Label>
              <textarea
                data-testid="cancel-feedback-input"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={t("profile.cancelFeedbackPh")}
                maxLength={500}
                rows={3}
                className="w-full border border-stone-200 rounded-sm p-2 text-sm resize-none focus:outline-none focus:border-[#E05236]"
              />
              <div className="text-[10px] text-neutral-400 text-right font-mono-plex mt-1">
                {feedback.length} / 500
              </div>
            </div>

            {/* Removed: duplicate warning subtitle. The action buttons are self-explanatory. */}

            <div className="flex gap-2 pt-2">
              <Button
                data-testid="cancel-sub-keep-btn"
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                className="flex-1 rounded-sm h-11"
              >
                {t("profile.cancelKeepBtn")}
              </Button>
              <Button
                data-testid="cancel-sub-confirm-btn"
                onClick={handleConfirm}
                disabled={submitting || !reason}
                className="flex-1 rounded-sm bg-red-600 hover:bg-red-700 text-white h-11"
              >
                {submitting ? t("profile.cancelCanceling") : t("profile.cancelConfirmBtn")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
